import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, ArrowRight, Crown, Search as SearchIcon, Star, FileDown, Users } from 'lucide-react';
import SquareLoader from './SquareLoader'; // Ensure import exists
import VaultOnboarding from './VaultOnboarding';
import { useGroupContext } from '../lib/groups/context';
import { verifyInviteCodeCore, listInvitesForEmailCore, listUserVaultsWithRoleCore, listGroupsByIdsCore } from '@gv/core';
import { getPreferredVaultIdCore, setPreferredVaultIdCore } from '@gv/core';
export default function HomePage({ user }: HomePageProps) {
    const [userVaults, setUserVaults] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [needsPassword, setNeedsPassword] = useState(false);
    const [highlightInvites, setHighlightInvites] = useState(false);
    const [pendingInvites, setPendingInvites] = useState<Array<{ id: string; group_id: string; token: string; created_at: string; email?: string | null; vault?: { name?: string; slug?: string | null; image_url?: string | null }, inviter_name?: string | null }>>([]);
    const [pendingInvitesError, setPendingInvitesError] = useState<string>('');
    const [otpByToken, setOtpByToken] = useState<Record<string, string>>({});
    const [acceptStatus, setAcceptStatus] = useState<Record<string, 'idle' | 'verifying' | 'accepted' | 'error'>>({});
    const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});
    // Simple ticker stub (kept in case we reintroduce timers)
    const [nowMs] = useState<number>(() => Date.now());
    // Input refs so we can focus next invite after accepting
    const inputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});
    const { reload } = useGroupContext();
    const [preferredVaultId, setPreferredVaultId] = useState<string | null>(null);

    useEffect(() => {
        loadUserVaults();
        loadPendingInvites();

        // Check if user wants to create a vault
        const params = new URLSearchParams(window.location.search);
        if (params.get('create') === 'true') {
            setShowOnboarding(true);
        }
        try {
            const flag = localStorage.getItem('gv:needsPasswordSetup');
            setNeedsPassword(flag === '1');
        } catch { setNeedsPassword(false); }

        // Restore last find query (if returning from Access Denied)
        try {
            const last = sessionStorage.getItem('gv:lastFindQuery');
            if (last) setQuickFind(last);
        } catch { /* ignore */ }

        // If a flag requests focusing invites, scroll and briefly highlight the section
        try {
            const focus = sessionStorage.getItem('gv:focusInvites');
            if (focus === '1') {
                sessionStorage.removeItem('gv:focusInvites');
                setTimeout(() => {
                    const el = document.getElementById('invites-section');
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                    setHighlightInvites(true);
                    setTimeout(() => setHighlightInvites(false), 1500);
                }, 50);
            }
        } catch { /* ignore */ }
    }, [user]);

    // No send-code timers needed in manual code flow

    const loadUserVaults = async () => {
        try {
            const vaults = await listUserVaultsWithRoleCore(supabase as any, user.id);
            setUserVaults(vaults as any[]);
            try {
                const pref = await getPreferredVaultIdCore(supabase as any, user.id);
                setPreferredVaultId(pref);
            } catch { /* ignore */ }
        } catch (error) {
            console.error('Error loading user vaults:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPendingInvites = async () => {
        try {
            const myEmail = (user?.email || '').toLowerCase();
            if (!myEmail) { setPendingInvites([]); return; }
            const list = await listInvitesForEmailCore(supabase as any, myEmail) as any as Array<{ id: string; group_id: string; token: string; created_at: string; email: string }>;
            <SquareLoader size="sm" />
            if (list.length === 0) { setPendingInvites([]); return; }
            // Enrich with vault info
            const groupIds = Array.from(new Set(list.map(i => i.group_id)));
            // Exclude vaults the user is already a member of
            let joined = new Set<string>();
            try {
                const mems = await listUserVaultsWithRoleCore(supabase as any, user.id);
                for (const m of (mems || []) as any[]) joined.add(m.id);
            } catch { /* ignore */ }
            let vaultById: Record<string, { name?: string; slug?: string | null; image_url?: string | null }> = {};
            try {
                const vaults = await listGroupsByIdsCore(supabase as any, groupIds);
                for (const v of (vaults || []) as any[]) {
                    vaultById[v.id] = { name: v.name, slug: v.slug, image_url: v.image_url };
                }
            } catch { /* ignore enrich errors */ }
            const enriched = list
                .filter(i => !joined.has(i.group_id)) // user already a member => hide
                .map(i => ({ ...i, vault: vaultById[i.group_id] || undefined }));
            // Do NOT drop invites without vault metadata; RLS may block vaults until acceptance
            setPendingInvites(enriched as any);
            setPendingInvitesError('');
        } catch (e) {
            console.warn('Failed to load pending invites', e);
            setPendingInvites([]);
            setPendingInvitesError('Failed to load invites. Please ensure database migrations and RLS are applied.');
        }
    };

    const joinVault = (slug: string) => {
        try {
            const target = `/${slug}`;
            window.history.pushState({}, '', target);
            // Inform the group context to re-evaluate access without a full reload
            reload();
        } catch {
            // Fallback to hard navigation if history API fails
            window.location.href = `/${slug}`;
        }
    };

    const openInviteFromToken = (token: string) => {
        try {
            const url = new URL(window.location.href);
            url.searchParams.set('invite', token);
            window.history.pushState({}, '', url.toString());
            reload();
        } catch {
            window.location.href = `/?invite=${encodeURIComponent(token)}`;
        }
    };

    const createNewVault = () => {
        setShowOnboarding(true);
    };
    const [quickFind, setQuickFind] = useState('');
    const findVault = () => {
        setShowOnboarding('find' as any);
    };
    const goToVault = (slug: string) => {
        // Normalize: strip all slashes anywhere, convert spaces to dashes, lowercase, collapse multiple dashes
        const clean = (slug || '')
            .trim()
            .replace(/\//g, '')
            .replace(/\s+/g, '-')
            .toLowerCase()
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
        if (!clean) return;
        try { sessionStorage.setItem('gv:lastFindQuery', slug); } catch { }
        try {
            window.history.pushState({}, '', `/${clean}`);
            reload();
        } catch {
            window.location.href = `/${clean}`;
        }
    };

    // Show vault creation onboarding
    if (showOnboarding) {
        const initial = showOnboarding === ('find' as any) ? 'find-vault' : 'create-vault';
        return <VaultOnboarding initialStep={initial as any} onComplete={() => setShowOnboarding(false)} />;
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    <img
                        src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                        alt="Vault Contact Logo"
                        className="h-16 w-16 mx-auto mb-6 object-contain animate-pulse"
                    />
                    <div className="mb-4 flex justify-center">
                        <SquareLoader size="sm" />
                    </div>
                    <p className="text-gray-400 font-light tracking-wide">LOADING VAULTS...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black">
            {/* Password setup banner for magic-link signups */}
            {needsPassword && (
                <div className="bg-yellow-900/30 border-b border-yellow-800/60 text-yellow-100">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                        <div className="text-sm">For security, please set a password for your account.</div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    try { localStorage.removeItem('gv:needsPasswordSetup'); } catch { }
                                    try { window.location.href = '/auth/reset-password#type=recovery&recovery=true'; } catch { }
                                }}
                                className="px-3 py-1 bg-white text-black text-xs font-semibold hover:bg-gray-100"
                            >
                                Set Password
                            </button>
                            <button
                                onClick={() => { try { localStorage.removeItem('gv:needsPasswordSetup'); setNeedsPassword(false); } catch { } }}
                                className="px-3 py-1 border border-yellow-700 text-yellow-100 text-xs hover:bg-yellow-800/30"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Header - compact, mobile-first */}
            <header className="border-b border-gray-800 px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                            alt="Vault Contact Logo"
                            className="h-7 w-7 object-contain"
                        />
                        <div>
                            <h1 className="text-xl sm:text-2xl font-light text-white tracking-wide">VAULT CONTACT</h1>
                            <p className="hidden sm:block text-gray-400 text-xs">Private Contact Management</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:block text-right">
                            <p className="text-white text-sm font-medium truncate max-w-[180px]">{user?.email}</p>
                            <p className="text-gray-400 text-xs">Authenticated</p>
                        </div>
                        <button
                            onClick={async () => { try { await supabase.auth.signOut(); window.location.href = '/'; } catch { window.location.href = '/'; } }}
                            className="hidden sm:inline-flex text-xs px-3 py-1 border border-gray-700 text-gray-300 hover:bg-gray-800 rounded"
                            title="Sign out"
                        >
                            Sign Out
                        </button>
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    </div>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
                {/* Search Section */}
                <div className="bg-black border border-gray-800 p-4 sm:p-5">
                    <div className="flex items-center gap-3">
                        <div className="relative w-full">
                            <input
                                type="text"
                                value={quickFind}
                                onChange={(e) => setQuickFind(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') goToVault(quickFind); }}
                                placeholder="Search"
                                className="w-full pl-3 pr-12 py-2.5 bg-gray-900 border border-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                                aria-label="Find Vault"
                            />
                            <div className="absolute inset-y-0 right-0 pr-2 flex items-center">
                                <button
                                    onClick={() => goToVault(quickFind)}
                                    aria-label="Search"
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gray-800 hover:bg-gray-700 text-white border border-gray-700"
                                >
                                    <SearchIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* My Vaults + Create CTA */}
                <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h3 className="text-xl sm:text-2xl font-light text-white">My Vaults</h3>
                        <div className="flex gap-2">
                            <button
                                onClick={() => window.open('/export-help', '_blank')}
                                className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                title="Contact Export Help"
                            >
                                <FileDown className="w-4 h-4 mr-2" />
                                Export Guide
                            </button>
                            <button
                                onClick={createNewVault}
                                className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Create Vault
                            </button>
                        </div>
                    </div>

                    {userVaults.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            {userVaults.map((vault) => (
                                <div
                                    key={vault.id}
                                    className="text-left bg-gray-900 border border-gray-800 rounded-lg p-4 hover:bg-gray-800 transition-colors cursor-pointer"
                                    onClick={() => joinVault(vault.slug)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gray-800 rounded-full overflow-hidden flex items-center justify-center">
                                            {(() => {
                                                const base = vault?.image_url as string | undefined;
                                                if (!base) return <Crown className="w-5 h-5 text-gray-400" />;
                                                let url = base;
                                                try {
                                                    const b = localStorage.getItem(`gv:logoBust:${vault.id}`);
                                                    if (b) url = `${base}${base.includes('?') ? '&' : '?'}v=${encodeURIComponent(b)}`;
                                                } catch { /* ignore */ }
                                                return <img src={url} alt={vault?.name || 'Vault'} className="w-full h-full object-cover" />;
                                            })()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white font-medium truncate">{vault.name}</div>
                                            <div className="text-gray-400 text-xs">/{vault.slug}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {preferredVaultId === vault.id ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-yellow-900 text-yellow-300 border border-yellow-800">
                                                    <Star className="w-3.5 h-3.5" /> Default
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="text-[10px] px-2 py-0.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-800"
                                                    title="Set as default"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        try {
                                                            await setPreferredVaultIdCore(supabase as any, user.id, vault.id);
                                                            setPreferredVaultId(vault.id);
                                                        } catch { /* ignore */ }
                                                    }}
                                                >
                                                    Set default
                                                </button>
                                            )}
                                            <ArrowRight className="w-4 h-4 text-gray-400" />
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${vault.role === 'owner' ? 'bg-yellow-900 text-yellow-300' : 'bg-blue-900 text-blue-300'}`}>
                                            {vault.role === 'owner' ? 'Owner' : 'Member'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-10 bg-gray-900 border border-gray-800 rounded-lg">
                            <Crown className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <h4 className="text-white text-base sm:text-lg font-medium mb-2">No Vaults Yet</h4>
                            <p className="text-gray-400 text-sm mb-4">Create your first vault</p>
                            <div className="flex items-center justify-center">
                                <button
                                    onClick={createNewVault}
                                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Your First Vault
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Invites (below My Vaults) */}
                <div id="invites-section" className={`space-y-3 ${highlightInvites ? 'ring-1 ring-blue-500 rounded-md ring-offset-2 ring-offset-black transition' : ''}`}>
                    <h3 className="text-xl sm:text-2xl font-light text-white">Invites</h3>
                    {pendingInvitesError && (
                        <div className="p-3 bg-red-900/30 border border-red-800/60 text-red-200 text-sm rounded">
                            {pendingInvitesError}
                        </div>
                    )}
                    {pendingInvites.length > 0 ? (
                        // Widen cards slightly by delaying the 3rd column until xl and allow more room for actions
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                            {pendingInvites.map((inv) => (
                                <div key={inv.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 sm:p-5 flex flex-col gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center">
                                            {inv.vault?.image_url ? (
                                                <img src={inv.vault.image_url} alt={inv.vault?.name || 'Vault'} className="w-full h-full object-cover" />
                                            ) : (
                                                <img src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp" alt="Vault" className="w-6 h-6 opacity-80" />
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-white font-medium truncate">{inv.vault?.name || 'Vault Invitation'}</div>
                                            <div className="text-gray-400 text-xs truncate">
                                                {inv.email}
                                            </div>
                                        </div>
                                    </div>
                                    {/*
                                                                            Controls row: use column on small screens; switch to row at md to avoid cramping at sm widths,
                                                                            and allow wrapping so buttons drop to a new line if space is tight.
                                                                        */}
                                    <div className="flex flex-col md:flex-row flex-wrap items-stretch md:items-center gap-2 md:gap-3 w-full">
                                        <div className="relative w-full md:flex-1 md:min-w-[12rem] min-w-0">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                placeholder="6-digit code"
                                                value={otpByToken[inv.token] || ''}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                                                    setOtpByToken(prev => ({ ...prev, [inv.token]: v }));
                                                    setAcceptStatus(prev => ({ ...prev, [inv.token]: 'idle' }));
                                                    setInviteErrors(prev => ({ ...prev, [inv.token]: '' }));
                                                }}
                                                onPaste={(e) => {
                                                    try {
                                                        const txt = e.clipboardData?.getData('text') || '';
                                                        const m = txt.match(/\b(\d{6})\b/);
                                                        if (m) {
                                                            e.preventDefault();
                                                            const code = m[1];
                                                            setOtpByToken(prev => ({ ...prev, [inv.token]: code }));
                                                            setAcceptStatus(prev => ({ ...prev, [inv.token]: 'idle' }));
                                                        }
                                                    } catch { /* ignore */ }
                                                }}
                                                onFocus={async () => {
                                                    try {
                                                        if ((otpByToken[inv.token] || '').length) return;
                                                        const txt = await navigator.clipboard.readText();
                                                        const m = txt.match(/\b(\d{6})\b/);
                                                        if (m) {
                                                            const code = m[1];
                                                            setOtpByToken(prev => ({ ...prev, [inv.token]: code }));
                                                            setAcceptStatus(prev => ({ ...prev, [inv.token]: 'idle' }));
                                                        }
                                                    } catch { /* permissions may block; ignore */ }
                                                }}
                                                className="w-full px-3 pr-8 py-2 bg-gray-800 border border-gray-700 text-white rounded-md tracking-widest"
                                                aria-label="Invite code"
                                                ref={(el) => { inputRefs.current[inv.token] = el; }}
                                            />
                                            {Boolean((otpByToken[inv.token] || '').length) && (
                                                <button
                                                    type="button"
                                                    onClick={() => setOtpByToken(prev => ({ ...prev, [inv.token]: '' }))}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 px-1"
                                                    aria-label="Clear code"
                                                    title="Clear"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            onClick={async () => {
                                                const code = (otpByToken[inv.token] || '').trim();
                                                if (code.length < 6) {
                                                    setInviteErrors(prev => ({ ...prev, [inv.token]: 'Enter the 6-digit code from the inviter.' }));
                                                    return;
                                                }
                                                try {
                                                    setAcceptStatus(prev => ({ ...prev, [inv.token]: 'verifying' }));
                                                    setInviteErrors(prev => ({ ...prev, [inv.token]: '' }));
                                                    const ok = await verifyInviteCodeCore(supabase as any, inv.token, code);
                                                    if (!ok) {
                                                        // Local mismatch between entered code and token on the invite
                                                        setAcceptStatus(prev => ({ ...prev, [inv.token]: 'error' }));
                                                        setInviteErrors(prev => ({ ...prev, [inv.token]: 'Wrong code—double-check and try again.' }));
                                                        return;
                                                    }
                                                    // Accepted -> remove from local UI immediately
                                                    setAcceptStatus(prev => ({ ...prev, [inv.token]: 'accepted' }));
                                                    setInviteErrors(prev => ({ ...prev, [inv.token]: '' }));
                                                    setPendingInvites(prev => prev.filter(p => p.id !== inv.id));
                                                    setOtpByToken(prev => { const next = { ...prev }; delete next[inv.token]; return next; });
                                                    setAcceptStatus(prev => { const next = { ...prev }; delete next[inv.token]; return next; });
                                                    setInviteErrors(prev => { const next = { ...prev }; delete next[inv.token]; return next; });
                                                    // Re-sync data in the background (RLS + membership)
                                                    try { await reload(); } catch { }
                                                    try { await loadUserVaults(); } catch { }
                                                    setTimeout(() => { loadPendingInvites().catch(() => { }); }, 100);
                                                    return;
                                                } catch (e: any) {
                                                    const raw = (e?.message || e?.error_description || '').toString();
                                                    console.error('accept invite failed', e);
                                                    // Surface backend message directly for development (no fallbacks)
                                                    const friendly = raw || 'Could not accept invite.';
                                                    setInviteErrors(prev => ({ ...prev, [inv.token]: friendly }));
                                                    setAcceptStatus(prev => ({ ...prev, [inv.token]: 'error' }));
                                                }
                                            }}
                                            className="px-3 py-2 rounded-md border border-gray-700 text-gray-200 hover:bg-gray-800 text-sm inline-flex items-center justify-center gap-2 w-full md:w-auto md:min-w-[110px]"
                                        >
                                            {acceptStatus[inv.token] === 'verifying' ? (
                                                <>
                                                    <SquareLoader size="xs" />
                                                    <span className="ml-2">Verifying…</span>
                                                </>
                                            ) : (
                                                <span>Accept</span>
                                            )}
                                        </button>

                                        {/* Date on the right side of the Accept button */}
                                        <div className="text-gray-500 text-[10px] md:ml-auto ml-0 self-center">
                                            {new Date(inv.created_at).toLocaleDateString()}
                                        </div>

                                        {/* Decline removed in simplified model; only host can cancel. */}
                                    </div>
                                    {(() => {
                                        const msg = inviteErrors[inv.token];
                                        if (!msg) return null;
                                        return (
                                            <div className="text-xs text-red-300 flex items-center gap-2 flex-wrap">
                                                <span>{msg}</span>
                                            </div>
                                        );
                                    })()}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 bg-gray-900 border border-gray-800 rounded-lg opacity-60">
                            <div className="text-white/70 font-medium">No invites</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
